<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { TEXT_CHARS_LIMIT } from '@/domain/menu-import/capture-files';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Escriba o pegue su menú» (plan §4.3): replaces the source cards in
   * place. Its «Ordenar mi menú» is the screen's one orange button (the
   * bottom bar hides its own CTA while this card is open).
   */
  const MIN_CHARS = 10;

  const props = withDefaults(
    defineProps<{
      busy: boolean;
      /** Offline: sending is not possible. */
      disabled?: boolean;
      /** Why the last «Ordenar mi menú» did not go through (the text is kept). */
      error?: string | null;
    }>(),
    { disabled: false, error: null }
  );
  const emit = defineEmits<{
    (e: 'submit', text: string): void;
    (e: 'cancel'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const titleId = nextDomId('menu-paste-title');

  const text = ref('');
  const count = computed(() =>
    new Intl.NumberFormat(proxy.$i18n.locale).format(text.value.length)
  );
  const atLimit = computed(() => text.value.length >= TEXT_CHARS_LIMIT);
  const canSubmit = computed(
    () =>
      text.value.trim().length >= MIN_CHARS && !props.busy && !props.disabled
  );

  function submit() {
    if (canSubmit.value) emit('submit', text.value);
  }
</script>

<template>
  <section class="v2-menu-paste-card" :aria-labelledby="titleId">
    <h2 :id="titleId" class="paste-title">
      {{ $t('menuImport.paste.title') }}
    </h2>
    <PosUiTextarea
      class="paste-input"
      :value="text"
      :rows="10"
      :maxlength="TEXT_CHARS_LIMIT"
      :placeholder="$t('menuImport.paste.placeholder')"
      :disabled="busy"
      :aria-labelledby="titleId"
      @input="text = $event"
    />
    <div class="paste-meta">
      <p v-if="atLimit" class="paste-limit" role="status">
        {{ $t('menuImport.paste.tooLong') }}
      </p>
      <p class="paste-count">
        {{ $t('menuImport.paste.count', { count }) }}
      </p>
    </div>
    <p v-if="error" class="paste-error" role="alert">
      <PosUiIcon name="alert-circle" class="error-icon" />
      <span>{{ error }}</span>
    </p>
    <div class="paste-actions">
      <PosUiButton
        color="neutral"
        variant="ghost"
        :disabled="busy"
        @click="emit('cancel')"
        >{{ $t('menuImport.paste.cancel') }}</PosUiButton
      >
      <PosUiButton
        class="paste-submit"
        :disabled="!canSubmit"
        :loading="busy"
        @click="submit"
        >{{ $t('menuImport.paste.submit') }}</PosUiButton
      >
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-paste-card {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;
    .paste-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .paste-input {
      @apply tw-text-base md:tw-text-sm;
    }
    .paste-meta {
      @apply tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-x-4 tw-gap-y-1;
    }
    .paste-limit {
      @apply tw-m-0 tw-min-w-0 tw-flex-1 tw-text-xs tw-font-medium tw-text-amber-800;
    }
    .paste-count {
      @apply tw-m-0 tw-ml-auto tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-neutral-500;
    }
    .paste-error {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-700;
      .error-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
      }
    }
    .paste-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2;
    }
    .paste-submit {
      @apply tw-min-w-0;
    }
  }
</style>
